/**
 * Tāmēt.lv - Main JavaScript
 * ES Module entry point
 */

// Import all modules
import * as utils from './modules/utils.js';
import * as validation from './modules/validation.js';
import * as forms from './modules/forms.js';
import * as notifications from './modules/notifications.js';
import * as modal from './modules/modal.js';
import * as languageSwitcher from './modules/language-switcher.js';
import * as organizationSwitcher from './modules/organization-switcher.js';
import * as calculations from './modules/calculations.js';
import * as lineItemManagement from './modules/line_item_management.js';
import * as quoteAutosave from './modules/quote_autosave.js';
import * as verification from './modules/verification.js';
import * as filters from './modules/filters.js';
import * as listActions from './modules/list_actions.js';
import * as charts from './modules/charts.js';
import * as htmxIndicators from './modules/htmx_indicators.js';
import * as inlineEditing from './inline_editing.js';
import * as groupManagement from './group_management.js';
import * as quoteCreate from './quote_create.js';

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    initializeApp();
});

// Run one module's init without letting its failure abort the others: a
// single throw used to skip every later module (inline editing, autosave,
// filters, charts...) for the whole page.
function safeInit(name, fn) {
    if (typeof fn !== 'function') return;
    try {
        fn();
    } catch (error) {
        console.error(`Initialising ${name} failed:`, error);
    }
}

function initializeApp() {
    // Initialize modules that have init functions
    safeInit('languageSwitcher', languageSwitcher.init);
    safeInit('organizationSwitcher', organizationSwitcher.init);
    safeInit('forms', forms.initFormEnhancements);
    safeInit('notifications', notifications.init);
    safeInit('modal', modal.init);
    safeInit('calculations', calculations.init);
    safeInit('lineItemManagement', lineItemManagement.init);
    safeInit('quoteAutosave', quoteAutosave.init);
    safeInit('inlineEditing', inlineEditing.init);
    safeInit('groupManagement', groupManagement.init);
    safeInit('filters', filters.init);
    safeInit('listActions', listActions.init);
    safeInit('charts', charts.init);
    safeInit('htmxIndicators', htmxIndicators.init);

    // Initialize built-in features
    initializeMobileNav();
    initializeUserMenu();
    initializeMessages();
    initializeForms();
    initializeSmoothScroll();
    initializeHTMXHandlers();
    initializeCopyButtons();
}

// Global HTMX handlers
function initializeHTMXHandlers() {
    // Attach the CSRF token to every unsafe HTMX request in one place, so all
    // HTMX POST/PUT/PATCH/DELETE calls are protected regardless of which page
    // issued them (no reliance on per-page inline scripts). The listener is
    // safe to register even before htmx has loaded.
    document.body.addEventListener('htmx:configRequest', function(evt) {
        const method = (evt.detail.verb || '').toUpperCase();
        if (method && method !== 'GET' && method !== 'HEAD') {
            const token = utils.getCsrfToken();
            if (token) {
                evt.detail.headers['X-CSRFToken'] = token;
            }
        }
    });
}

function initializeCopyButtons() {
    document.addEventListener('click', async function(event) {
        const trigger = event.target.closest('[data-copy-text]');
        if (!trigger) {
            return;
        }

        event.preventDefault();
        const value = (trigger.getAttribute('data-copy-text') || '').trim();
        if (!value) {
            return;
        }

        let success = false;
        try {
            success = await utils.copyToClipboard(value);
        } catch (error) {
            success = false;
        }

        if (success) {
            const successMessage = trigger.getAttribute('data-copy-success');
            if (successMessage && notifications.success) {
                notifications.success(successMessage, { duration: 2500 });
            }
        } else {
            const errorMessage = trigger.getAttribute('data-copy-error');
            if (errorMessage && notifications.error) {
                notifications.error(errorMessage, { duration: 3000 });
            }
        }
    });
}

    // Mobile navigation toggle
    function initializeMobileNav() {
        const toggle = document.querySelector('.navbar__toggle');
        const menu = document.querySelector('.navbar__menu');
        
        if (!toggle || !menu) return;
        
        // Make toggle function globally available
        window.toggleMobileMenu = function() {
            const isOpen = menu.classList.contains('is-open');
            
            if (isOpen) {
                closeMobileMenu();
            } else {
                openMobileMenu();
            }
        };
        
        function openMobileMenu() {
            menu.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
            document.body.style.overflow = 'hidden';
            
            // Close on click outside
            setTimeout(() => {
                document.addEventListener('click', handleOutsideClick);
            }, 100);
        }
        
        function closeMobileMenu() {
            menu.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
            document.body.style.overflow = '';
            document.removeEventListener('click', handleOutsideClick);
        }
        
        function handleOutsideClick(event) {
            if (!menu.contains(event.target) && !toggle.contains(event.target)) {
                closeMobileMenu();
            }
        }
        
        // Close menu on escape key
        document.addEventListener('keydown', function(event) {
            if (event.key === 'Escape' && menu.classList.contains('is-open')) {
                closeMobileMenu();
                toggle.focus();
            }
        });
        
        // Close menu on window resize to desktop size
        window.addEventListener('resize', utils.debounce(function() {
            if (window.innerWidth >= 768 && menu.classList.contains('is-open')) {
                closeMobileMenu();
            }
        }, 250));
    }

    // User menu dropdown
    function initializeUserMenu() {
        const toggle = document.querySelector('.navbar__user-toggle');
        const dropdown = document.querySelector('.navbar__user-dropdown');
        const arrow = document.querySelector('.navbar__user-arrow');
        
        if (!toggle || !dropdown) return;
        
        // Make toggle function globally available
        window.toggleUserMenu = function() {
            const isOpen = dropdown.classList.contains('is-open');
            
            if (isOpen) {
                closeUserMenu();
            } else {
                openUserMenu();
            }
        };
        
        function openUserMenu() {
            dropdown.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
            if (arrow) arrow.style.transform = 'rotate(180deg)';
            
            // Focus first menu item
            const firstItem = dropdown.querySelector('.navbar__dropdown-link');
            if (firstItem) firstItem.focus();
            
            // Close on click outside
            setTimeout(() => {
                document.addEventListener('click', handleOutsideClick);
            }, 100);
        }
        
        function closeUserMenu() {
            dropdown.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
            if (arrow) arrow.style.transform = '';
            document.removeEventListener('click', handleOutsideClick);
        }
        
        function handleOutsideClick(event) {
            if (!dropdown.contains(event.target) && !toggle.contains(event.target)) {
                closeUserMenu();
            }
        }
        
        // Handle keyboard navigation
        dropdown.addEventListener('keydown', function(event) {
            const items = dropdown.querySelectorAll('.navbar__dropdown-link, .navbar__logout-btn');
            const currentIndex = Array.from(items).indexOf(document.activeElement);
            
            switch (event.key) {
                case 'Escape':
                    closeUserMenu();
                    toggle.focus();
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
                    items[nextIndex].focus();
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
                    items[prevIndex].focus();
                    break;
            }
        });
    }

    // Auto-dismiss messages after 5 seconds
    function initializeMessages() {
        const messages = document.querySelectorAll('.message');
        
        messages.forEach(function(message) {
            // Auto-dismiss after 5 seconds
            setTimeout(function() {
                if (message.parentNode) {
                    dismissMessage(message);
                }
            }, 5000);
        });
        
        function dismissMessage(message) {
            message.style.animation = 'slideOut 0.3s ease-in forwards';
            setTimeout(function() {
                if (message.parentNode) {
                    message.remove();
                }
            }, 300);
        }
    }

    // Form enhancements
    function initializeForms() {
        // Auto-resize textareas
        const textareas = document.querySelectorAll('textarea');
        textareas.forEach(function(textarea) {
            autoResizeTextarea(textarea);
            textarea.addEventListener('input', function() {
                autoResizeTextarea(textarea);
            });
        });
        
        function autoResizeTextarea(textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = Math.max(88, textarea.scrollHeight) + 'px';
        }
    }

    // Touch feedback is handled via CSS :active pseudo-class and .touch-active in main.css
    // Button sizing is handled via CSS min-h-[22px] touch-manipulation in .btn utility

    // Initialize smooth scrolling
    function initializeSmoothScroll() {
        document.querySelectorAll('a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', function(e) {
                const href = this.getAttribute('href');
                
                // Skip if href is just '#' or empty
                if (!href || href === '#' || href.length <= 1) {
                    return;
                }
                
                e.preventDefault();
                const target = document.querySelector(href);
                if (target) {
                    utils.scrollToElement(target);
                }
            });
        });
    }

// Make certain functions globally available for inline scripts and HTMX
window.FreeCRM = {
    utils,
    validation,
    forms,
    notifications,
    modal,
    calculations,
    quoteCreate,
    // Legacy compatibility function
    showNotification: function(message, type = 'info') {
        notifications.notify(message, type);
    }
};

// HTMX response snippets (e.g. customer quick-create) call a global
// showNotification; pages without their own definition use the shared one.
if (typeof window.showNotification !== 'function') {
    window.showNotification = window.FreeCRM.showNotification;
}

// Expose line item management functions globally for template onclick handlers
window.toggleAddLineItemForm = lineItemManagement.toggleAddLineItemForm;
// Note: editLineItem and cancelEditLineItem are handled via inline_editing.js
